import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { api } from '../lib/api'
import type { TheorySummary } from '../lib/types'

interface AudioState {
  track: TheorySummary | null
  queue: TheorySummary[]
  playing: boolean
  loading: boolean
  currentTime: number
  duration: number
  buffered: number
  rate: number
  volume: number
  muted: boolean
  error: string | null
}

interface AudioContextValue extends AudioState {
  /** Starts a theory, or toggles it if it's already loaded. */
  /** `startAt` overrides the saved resume position — used by timestamp links. */
  play: (theory: TheorySummary, queue?: TheorySummary[], startAt?: number) => void
  toggle: () => void
  stop: () => void
  seek: (seconds: number) => void
  skip: (delta: number) => void
  setRate: (rate: number) => void
  setVolume: (volume: number) => void
  toggleMute: () => void
  isCurrent: (slug: string) => boolean
  hasNext: boolean
  playNext: () => void
}

const AudioPlayerContext = createContext<AudioContextValue | null>(null)

const RATES = [0.75, 1, 1.25, 1.5, 1.75, 2] as const
const STORE_RATE = 'sms-rate'
const STORE_VOLUME = 'sms-volume'
const STORE_POSITION = 'sms-positions'
/** Don't resume from a position within this many seconds of either end. */
const RESUME_EDGE_SECONDS = 10

const readJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

const writeJson = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* private mode / quota — playback still works, just without memory */
  }
}

/**
 * Owns the one `<audio>` element on the page.
 *
 * It lives above the router outlet so a recording keeps playing while you read
 * something else — which is the point now that most theories *are* the
 * recording. Everything that touches audio goes through here; a second element
 * anywhere else would let two theories play over each other.
 */
export function AudioProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const countedRef = useRef<Set<string>>(new Set())
  // Where to jump once metadata arrives. Held in a ref rather than a one-off
  // listener per track: starting a second theory before the first reported its
  // duration used to leave the old listener attached, and it would seek the new
  // recording to the previous one's saved position.
  const pendingSeekRef = useRef<number | null>(null)

  const [state, setState] = useState<AudioState>(() => ({
    track: null,
    queue: [],
    playing: false,
    loading: false,
    currentTime: 0,
    duration: 0,
    buffered: 0,
    rate: readJson(STORE_RATE, 1),
    volume: readJson(STORE_VOLUME, 1),
    muted: false,
    error: null,
  }))

  const patch = useCallback((next: Partial<AudioState>) => {
    setState((current) => ({ ...current, ...next }))
  }, [])

  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const element = new Audio()
      element.preload = 'metadata'
      audioRef.current = element
    }
    return audioRef.current
  }, [])

  const savePosition = useCallback((slug: string, seconds: number, duration: number) => {
    if (!slug || !Number.isFinite(seconds)) return
    const positions = readJson<Record<string, number>>(STORE_POSITION, {})
    // Finished (or barely started) recordings shouldn't strand a resume marker.
    if (seconds < RESUME_EDGE_SECONDS || (duration && seconds > duration - RESUME_EDGE_SECONDS)) {
      delete positions[slug]
    } else {
      positions[slug] = seconds
    }
    writeJson(STORE_POSITION, positions)
  }, [])

  // --- Element event wiring -------------------------------------------------

  useEffect(() => {
    const audio = getAudio()

    const onTime = () => patch({ currentTime: audio.currentTime })
    const onDuration = () => {
      patch({ duration: Number.isFinite(audio.duration) ? audio.duration : 0 })
      // Apply the resume point here, where the duration is finally known.
      const pending = pendingSeekRef.current
      if (pending != null) {
        pendingSeekRef.current = null
        if (!audio.duration || pending < audio.duration - RESUME_EDGE_SECONDS) {
          audio.currentTime = pending
        }
      }
    }
    const onPlay = () => patch({ playing: true, loading: false, error: null })
    const onPause = () => patch({ playing: false })
    const onWaiting = () => patch({ loading: true })
    const onPlaying = () => patch({ loading: false })
    const onProgress = () => {
      const end = audio.buffered.length ? audio.buffered.end(audio.buffered.length - 1) : 0
      patch({ buffered: end })
    }
    const onError = () =>
      patch({ playing: false, loading: false, error: 'That recording could not be played.' })

    const events = [
      ['timeupdate', onTime],
      ['durationchange', onDuration],
      ['loadedmetadata', onDuration],
      ['play', onPlay],
      ['pause', onPause],
      ['waiting', onWaiting],
      ['playing', onPlaying],
      ['progress', onProgress],
      ['error', onError],
    ] as const

    for (const [event, handler] of events) audio.addEventListener(event, handler)
    return () => {
      for (const [event, handler] of events) audio.removeEventListener(event, handler)
    }
  }, [getAudio, patch])

  // Persist the resume marker periodically and when the tab goes away.
  useEffect(() => {
    if (!state.track || !state.playing) return
    const slug = state.track.slug
    const timer = setInterval(() => {
      const audio = getAudio()
      savePosition(slug, audio.currentTime, audio.duration)
    }, 5000)

    const onHide = () => {
      const audio = getAudio()
      savePosition(slug, audio.currentTime, audio.duration)
    }
    window.addEventListener('pagehide', onHide)
    document.addEventListener('visibilitychange', onHide)

    return () => {
      clearInterval(timer)
      window.removeEventListener('pagehide', onHide)
      document.removeEventListener('visibilitychange', onHide)
      onHide()
    }
  }, [state.track, state.playing, getAudio, savePosition])

  // Count a listen once playback passes 20 seconds — enough to distinguish a
  // real listen from someone tapping play and immediately leaving.
  useEffect(() => {
    const slug = state.track?.slug
    if (!slug || countedRef.current.has(slug)) return
    if (state.currentTime > 20) {
      countedRef.current.add(slug)
      void api.countListen(slug)
    }
  }, [state.track?.slug, state.currentTime])

  const start = useCallback(
    (theory: TheorySummary, queue: TheorySummary[] = [], startAt?: number) => {
      if (!theory.audio?.url) return
      const audio = getAudio()

      audio.src = theory.audio.url
      audio.playbackRate = state.rate
      audio.volume = state.volume
      audio.muted = state.muted
      audio.load()

      // An explicit start time wins over the saved position: someone following
      // a link to 4:07 means 4:07, not wherever they happened to stop last time.
      const saved = readJson<Record<string, number>>(STORE_POSITION, {})[theory.slug]
      const resumeAt = startAt != null ? startAt : saved && saved > RESUME_EDGE_SECONDS ? saved : null
      pendingSeekRef.current = resumeAt

      patch({
        track: theory,
        queue,
        loading: true,
        error: null,
        currentTime: resumeAt ?? 0,
        duration: theory.audio.seconds ?? 0,
        buffered: 0,
      })

      void audio.play().catch(() => {
        // Autoplay policies reject play() that isn't user-initiated; surfacing
        // a paused state is friendlier than an error the visitor can't act on.
        patch({ playing: false, loading: false })
      })
    },
    [getAudio, patch, state.rate, state.volume, state.muted],
  )

  const toggle = useCallback(() => {
    const audio = getAudio()
    if (!state.track) return
    if (audio.paused) void audio.play().catch(() => patch({ playing: false }))
    else audio.pause()
  }, [getAudio, patch, state.track])

  const playNext = useCallback(() => {
    const index = state.queue.findIndex((item) => item.slug === state.track?.slug)
    const next = index >= 0 ? state.queue[index + 1] : undefined
    if (next) start(next, state.queue)
    else patch({ playing: false })
  }, [start, patch, state.queue, state.track?.slug])

  // Auto-advance through the queue when a recording finishes.
  useEffect(() => {
    const audio = getAudio()
    const onEnded = () => {
      if (state.track) savePosition(state.track.slug, 0, audio.duration)
      playNext()
    }
    audio.addEventListener('ended', onEnded)
    return () => audio.removeEventListener('ended', onEnded)
  }, [getAudio, playNext, savePosition, state.track])

  // Declared before `play`, which depends on it — a `const` referenced from a
  // dependency array before its own declaration is a temporal-dead-zone throw,
  // not a hoisted function.
  const seek = useCallback(
    (seconds: number) => {
      const audio = getAudio()
      const limit = Number.isFinite(audio.duration) ? audio.duration : seconds
      audio.currentTime = Math.min(Math.max(seconds, 0), limit)
      patch({ currentTime: audio.currentTime })
    },
    [getAudio, patch],
  )

  const play = useCallback(
    (theory: TheorySummary, queue: TheorySummary[] = [], startAt?: number) => {
      if (state.track?.slug === theory.slug) {
        // Already the current track. A timestamp jumps within it and keeps
        // playing; without one, this is the play/pause button doing its job.
        if (startAt != null) {
          seek(startAt)
          if (!state.playing) toggle()
          return
        }
        toggle()
        return
      }
      start(theory, queue, startAt)
    },
    [start, toggle, seek, state.track?.slug, state.playing],
  )

  const skip = useCallback((delta: number) => seek(getAudio().currentTime + delta), [getAudio, seek])

  const setRate = useCallback(
    (rate: number) => {
      getAudio().playbackRate = rate
      writeJson(STORE_RATE, rate)
      patch({ rate })
    },
    [getAudio, patch],
  )

  const setVolume = useCallback(
    (volume: number) => {
      const clamped = Math.min(Math.max(volume, 0), 1)
      const audio = getAudio()
      audio.volume = clamped
      audio.muted = false
      writeJson(STORE_VOLUME, clamped)
      patch({ volume: clamped, muted: false })
    },
    [getAudio, patch],
  )

  const toggleMute = useCallback(() => {
    const audio = getAudio()
    audio.muted = !audio.muted
    patch({ muted: audio.muted })
  }, [getAudio, patch])

  const stop = useCallback(() => {
    const audio = getAudio()
    if (state.track) savePosition(state.track.slug, audio.currentTime, audio.duration)
    audio.pause()
    audio.removeAttribute('src')
    audio.load()
    pendingSeekRef.current = null
    patch({
      track: null,
      queue: [],
      playing: false,
      currentTime: 0,
      duration: 0,
      buffered: 0,
      error: null,
    })
  }, [getAudio, patch, savePosition, state.track])

  // --- OS integration -------------------------------------------------------

  // Lock-screen artwork, hardware media keys, and headphone buttons. Worth the
  // wiring when the site is mostly something you listen to on a phone.
  useEffect(() => {
    if (!('mediaSession' in navigator) || !state.track) return
    const track = state.track

    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.title,
      artist: 'Max',
      album: 'Shit Max Says',
      artwork: track.coverImage
        ? [{ src: track.coverImage, sizes: '512x512', type: 'image/jpeg' }]
        : [],
    })

    const handlers: [MediaSessionAction, MediaSessionActionHandler][] = [
      ['play', () => toggle()],
      ['pause', () => toggle()],
      ['seekbackward', () => skip(-15)],
      ['seekforward', () => skip(30)],
      ['nexttrack', () => playNext()],
      ['seekto', (details) => details.seekTime != null && seek(details.seekTime)],
    ]

    for (const [action, handler] of handlers) {
      try {
        navigator.mediaSession.setActionHandler(action, handler)
      } catch {
        /* not every browser supports every action */
      }
    }

    return () => {
      for (const [action] of handlers) {
        try {
          navigator.mediaSession.setActionHandler(action, null)
        } catch {
          /* ignore */
        }
      }
    }
  }, [state.track, toggle, skip, seek, playNext])

  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = state.playing ? 'playing' : 'paused'
    }
  }, [state.playing])

  // Space toggles playback, Shift+arrows scrub — but never while typing, and
  // never when the key belongs to a button the visitor is actually focused on.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!state.track) return
      const target = event.target as HTMLElement | null
      if (target?.closest('input, textarea, select, button, a, [contenteditable="true"]')) return

      if (event.code === 'Space') {
        event.preventDefault()
        toggle()
      } else if (event.code === 'ArrowLeft' && event.shiftKey) {
        event.preventDefault()
        skip(-15)
      } else if (event.code === 'ArrowRight' && event.shiftKey) {
        event.preventDefault()
        skip(30)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [state.track, toggle, skip])

  const value = useMemo<AudioContextValue>(
    () => ({
      ...state,
      play,
      toggle,
      stop,
      seek,
      skip,
      setRate,
      setVolume,
      toggleMute,
      isCurrent: (slug: string) => state.track?.slug === slug,
      hasNext:
        state.queue.findIndex((item) => item.slug === state.track?.slug) < state.queue.length - 1,
      playNext,
    }),
    [state, play, toggle, stop, seek, skip, setRate, setVolume, toggleMute, playNext],
  )

  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>
}

export function useAudio() {
  const context = useContext(AudioPlayerContext)
  if (!context) throw new Error('useAudio must be used inside <AudioProvider>')
  return context
}

export { RATES }
